import React, { useState, useEffect, useRef } from "react";
import { Tabs, Input, Button, Tooltip, Spin, message } from "antd";
import { getTopicStats, renameTopic } from "../../../services/polly/classificationService";
import ConversationList from "./ConversationList";
import AddSubtopicModal from "./AddSubtopicModal";
import DisableModal from "./DisableModal";
import MoveTopicModal from "./MoveTopicModal";
import ConnectedJourneysDrawer from "./ConnectedJourneysDrawer";

const { TabPane } = Tabs;

const HEALTH_COLORS = {
  green: '#3B6D11',
  amber: '#BA7517',
  red: '#E24B4C',
};

function StatCard({ value, label, tooltip, valueClass }) {
  const labelContent = (
    <div className="ca-stat-label">
      {label}
      {tooltip && (
        <Tooltip title={tooltip} placement="bottom">
          <span className="ca-stat-tip">?</span>
        </Tooltip>
      )}
    </div>
  );
  return (
    <div className="ca-stat-card">
      {labelContent}
      <div className={`ca-stat-value${valueClass ? ' ' + valueClass : ''}`}>{value}</div>
    </div>
  );
}

function findBreadcrumb(topics, targetId, path = []) {
  for (const t of topics || []) {
    if (t.id === targetId) return path;
    if (t.children) {
      const found = findBreadcrumb(t.children, targetId, [...path, t.name]);
      if (found) return found;
    }
  }
  return null;
}

export default function TopicDetail({ topic, flatTopics, allTopics, period, onTopicUpdate }) {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(topic.name);
  const [renaming, setRenaming] = useState(false);
  const inputRef = useRef(null);
  const [addSubtopicVisible, setAddSubtopicVisible] = useState(false);
  const [disableVisible, setDisableVisible] = useState(false);
  const [moveVisible, setMoveVisible] = useState(false);
  const [journeysVisible, setJourneysVisible] = useState(false);

  useEffect(() => {
    setEditing(false);
    setEditName(topic.name);
    setStatsLoading(true);
    getTopicStats(topic.id, period)
      .then((res) => setStats(res))
      .catch((err) => {
        console.warn("Failed to load topic stats:", err);
        setStats(null);
      })
      .finally(() => setStatsLoading(false));
  }, [topic.id, topic.name, period]);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const startEditing = () => { setEditName(topic.name); setEditing(true); };
  const cancelEditing = () => { setEditing(false); setEditName(topic.name); };

  const saveRename = async () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === topic.name) { cancelEditing(); return; }
    setRenaming(true);
    try {
      await renameTopic(topic.id, trimmed);
      setEditing(false);
      message.success("Topic renamed");
      onTopicUpdate && onTopicUpdate();
    } catch (err) {
      message.error("Failed to rename topic");
    }
    setRenaming(false);
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') saveRename();
    if (e.key === 'Escape') cancelEditing();
  };

  const highPct = stats?.highConfidencePct ?? 0;
  const medPct = stats?.medConfidencePct ?? 0;
  const lowPct = stats?.lowConfidencePct ?? 0;
  const hasChildren = topic.children && topic.children.length > 0;
  const breadcrumb = findBreadcrumb(allTopics, topic.id);

  return (
    <div className="ca-detail">
      {/* Header */}
      <div className="ca-detail-header">
        {breadcrumb && breadcrumb.length > 0 && (
          <div className="ca-detail-breadcrumb">{breadcrumb.join(' > ')}</div>
        )}
        {editing ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Input
              ref={inputRef}
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={handleNameKeyDown}
              onBlur={cancelEditing}
              disabled={renaming}
              style={{ fontSize: 16, fontWeight: 500, maxWidth: 320 }}
            />
            <Button type="primary" size="small" loading={renaming}
              onMouseDown={(e) => e.preventDefault()} onClick={saveRename}>Save</Button>
            <span style={{ fontSize: 11, color: '#bbb' }}>Enter to save, Esc to cancel</span>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="ca-detail-title">{topic.name}</div>
            <span className="ca-chip" onClick={startEditing} style={{ fontSize: 11 }}>Rename</span>
          </div>
        )}
      </div>

      <Tabs defaultActiveKey="admin" className="ca-detail-tabs">
        <TabPane tab="Topic admin" key="admin">
          <Spin spinning={statsLoading}>
            {/* Stats */}
            <div className="ca-stat-row">
              <StatCard
                value={stats?.conversationCount ?? topic.conversationCount ?? 0}
                label="Conversations"
                tooltip="Total conversations classified under this topic in the selected period."
              />
              <StatCard
                value={stats ? `${stats.avgConfidence ?? 0}%` : '--'}
                label="Avg confidence"
                tooltip="Average classification confidence score across all conversations."
                valueClass={stats && stats.avgConfidence >= 75 ? 'green' : ''}
              />
              <StatCard
                value={stats ? `${stats.lowConfidencePct ?? 0}%` : '--'}
                label="Low confidence"
                tooltip="% of conversations classified here below 50% confidence. May need description refinement."
              />
            </div>

            {/* Confidence Bar */}
            <div className="ca-conf-wrap">
              <div className="ca-conf-header">
                <span>Confidence distribution</span>
                <span>High {highPct}% &middot; Med {medPct}% &middot; Low {lowPct}%</span>
              </div>
              <div className="ca-conf-track">
                {highPct > 0 && <div style={{ width: `${highPct}%`, background: HEALTH_COLORS.green }} />}
                {medPct > 0 && <div style={{ width: `${medPct}%`, background: HEALTH_COLORS.amber }} />}
                {lowPct > 0 && <div style={{ width: `${lowPct}%`, background: HEALTH_COLORS.red }} />}
              </div>
              <div className="ca-conf-legend">
                <span><span className="ca-conf-dot" style={{ background: HEALTH_COLORS.green }} />High (&gt;75%)</span>
                <span><span className="ca-conf-dot" style={{ background: HEALTH_COLORS.amber }} />Med (50-75%)</span>
                <span><span className="ca-conf-dot" style={{ background: HEALTH_COLORS.red }} />Low (&lt;50%)</span>
              </div>
            </div>

            {/* Actions */}
            <div className="ca-action-row">
              <span className="ca-chip" onClick={() => setMoveVisible(true)}>Move</span>
              <span className="ca-chip" onClick={() => setAddSubtopicVisible(true)}>Add Subtopic</span>
              <span className="ca-chip" onClick={() => setJourneysVisible(true)}>Connected Journeys</span>
              <span className="ca-chip danger" onClick={() => setDisableVisible(true)}>Disable</span>
            </div>

            {/* Subtopics */}
            {hasChildren && (
              <>
                <div className="ca-sec-divider">Subtopics</div>
                {topic.children.map((child) => {
                  const hk = child.highConfidencePct >= 80 ? 'green' : child.highConfidencePct >= 65 ? 'amber' : 'red';
                  return (
                    <div key={child.id} className="ca-child-card" style={{ borderLeftColor: HEALTH_COLORS[hk] }}>
                      <div className="ca-child-name">{child.name}</div>
                      <div className="ca-child-stats">
                        {child.conversationCount || 0} conversation{(child.conversationCount || 0) !== 1 ? 's' : ''}
                      </div>
                      {child.conversationCount > 0 && (
                        <div className="ca-child-conf">
                          <span className="ca-child-conf-high">{child.highConfidencePct || 0}% high</span>
                          <span className="ca-child-conf-med">{child.medConfidencePct || 0}% med</span>
                          <span className="ca-child-conf-low">{child.lowConfidencePct || 0}% low</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </Spin>
        </TabPane>

        <TabPane
          tab={<span>Conversations <span className="ca-tab-badge">{stats?.conversationCount ?? topic.conversationCount ?? 0}</span></span>}
          key="conversations"
        >
          <ConversationList topicId={topic.id} flatTopics={flatTopics} period={period} onTopicUpdate={onTopicUpdate} />
        </TabPane>
      </Tabs>

      <AddSubtopicModal visible={addSubtopicVisible} parentTopic={topic}
        onCancel={() => setAddSubtopicVisible(false)}
        onSuccess={() => { setAddSubtopicVisible(false); message.success("Subtopic added"); onTopicUpdate && onTopicUpdate(); }} />

      <DisableModal visible={disableVisible} topic={topic}
        onCancel={() => setDisableVisible(false)}
        onSuccess={() => { setDisableVisible(false); message.success(`${topic.name} disabled`); onTopicUpdate && onTopicUpdate(); }} />

      <MoveTopicModal visible={moveVisible} topic={topic} allTopics={allTopics}
        onCancel={() => setMoveVisible(false)}
        onSuccess={() => { setMoveVisible(false); onTopicUpdate && onTopicUpdate(); }} />

      <ConnectedJourneysDrawer visible={journeysVisible} topic={topic}
        onClose={() => setJourneysVisible(false)}
        onUpdate={onTopicUpdate} />
    </div>
  );
}
